import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Pill } from "@/components/kit";
import { usd } from "@/lib/format";
import { Plus, Save, X, Pencil, History, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

const empty = { sku: "", name: "", category: "shingles", unit: "square", material_cost_cents: 0, labor_cost_cents: 0, overhead_pct: 12, target_margin_pct: 30, image_url: "", description: "" };

export default function PriceBook() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/pricebook").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...edit,
      material_cost_cents: Math.round(Number(edit.material_cost_cents) * 100),
      labor_cost_cents: Math.round(Number(edit.labor_cost_cents) * 100),
      overhead_pct: Number(edit.overhead_pct), target_margin_pct: Number(edit.target_margin_pct) };
    try {
      if (edit.id) await api.put(`/pricebook/${edit.id}`, body);
      else await api.post("/pricebook", body);
      toast.success("Saved"); setEdit(null); load();
    } catch { toast.error("Failed"); }
  };

  const sell = (it) => {
    const base = it.material_cost_cents + it.labor_cost_cents;
    const cost = base * (1 + it.overhead_pct / 100);
    return Math.round(cost / (1 - it.target_margin_pct / 100));
  };

  return (
    <div>
      <PageHeader title="PriceBook" subtitle="Materials, labor, overhead & target margin per SKU — versioned"
        actions={<button data-testid="new-sku-btn" onClick={() => setEdit({ ...empty })} className="bg-brandred text-white font-bold py-2.5 px-5 rounded-xl border-2 border-brandred flex items-center gap-2 text-sm"><Plus className="w-4 h-4" /> New SKU</button>} />

      <div className="mb-card overflow-x-auto mb-scroll">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3"></th><th className="px-4 py-3">SKU</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Unit</th><th className="px-4 py-3 text-right">Material</th><th className="px-4 py-3 text-right">Labor</th><th className="px-4 py-3 text-right">OH%</th><th className="px-4 py-3 text-right">Margin%</th><th className="px-4 py-3 text-right">Sell</th><th className="px-4 py-3">v</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`sku-${it.sku}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2">{it.image_url ? <img src={it.image_url} alt="" className="w-9 h-9 rounded object-cover border border-slate-200" /> : <div className="w-9 h-9 rounded bg-slate-100" />}</td>
                <td className="px-4 py-3 font-num font-semibold text-navy">{it.sku}</td>
                <td className="px-4 py-3">{it.name}</td>
                <td className="px-4 py-3"><Pill>{it.unit}</Pill></td>
                <td className="px-4 py-3 text-right font-num">{usd(it.material_cost_cents)}</td>
                <td className="px-4 py-3 text-right font-num">{usd(it.labor_cost_cents)}</td>
                <td className="px-4 py-3 text-right font-num">{it.overhead_pct}%</td>
                <td className="px-4 py-3 text-right font-num text-navy font-bold">{it.target_margin_pct}%</td>
                <td className="px-4 py-3 text-right font-num text-successg font-bold">{usd(sell(it))}</td>
                <td className="px-4 py-3 text-xs text-slate-400 flex items-center gap-1"><History className="w-3 h-3" />{it.version}</td>
                <td className="px-2"><button onClick={() => setEdit({ ...it, material_cost_cents: (it.material_cost_cents || 0) / 100, labor_cost_cents: (it.labor_cost_cents || 0) / 100 })} data-testid={`edit-${it.sku}`} className="text-slate-400 hover:text-navy"><Pencil className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setEdit(null)}>
          <div className="mb-card p-6 w-full max-w-lg animate-scale-in" onClick={(e) => e.stopPropagation()} data-testid="sku-modal">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-2xl font-bold uppercase text-navy">{edit.id ? "Edit" : "New"} SKU</h3><button onClick={() => setEdit(null)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <div className="grid grid-cols-2 gap-3">
              {[["sku", "SKU", "text"], ["name", "Name", "text"], ["category", "Category", "text"], ["unit", "Unit", "text"],
                ["material_cost_cents", "Material ($)", "number"], ["labor_cost_cents", "Labor ($)", "number"],
                ["overhead_pct", "Overhead %", "number"], ["target_margin_pct", "Target Margin %", "number"]].map(([k, l, t]) => (
                <div key={k} className={k === "name" ? "col-span-2" : ""}>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</label>
                  <input data-testid={`sku-field-${k}`} type={t} value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}
                    className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy font-num" />
                </div>
              ))}
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description (shown on proposal)</label>
              <textarea data-testid="sku-field-description" rows={2} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" />
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Picture (for proposals)</label>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-16 h-16 rounded-lg border-2 border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">
                  {edit.image_url ? <img src={edit.image_url} alt="sku" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
                </div>
                <div className="flex-1">
                  <input data-testid="sku-image-file" type="file" accept="image/*" onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; if (file.size > 3 * 1024 * 1024) return toast.error("Image under 3 MB"); setEdit({ ...edit, image_url: await readFile(file) }); }} className="text-xs" />
                  <input data-testid="sku-image-url" value={edit.image_url?.startsWith("data:") ? "" : (edit.image_url || "")} onChange={(e) => setEdit({ ...edit, image_url: e.target.value })} placeholder="…or paste image URL" className="mt-1 w-full border-2 border-slate-200 rounded-xl px-2 py-1.5 text-xs outline-none focus:border-navy" />
                  {edit.image_url && <button onClick={() => setEdit({ ...edit, image_url: "" })} className="text-[11px] text-brandred font-semibold mt-1">Remove picture</button>}
                </div>
              </div>
            </div>
            <button data-testid="save-sku" onClick={save} className="w-full mt-4 bg-navy text-white font-bold py-3 rounded-xl border-2 border-navy flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Save (creates new version)</button>
          </div>
        </div>
      )}
    </div>
  );
}
